import { RefreshResponse, RefreshResponseItem } from '../types/items.api';

import create from 'zustand';

type OperationType = 'refresh' | 'reset';

interface GroupedItems {
   [parentItemNo: string]: RefreshResponseItem[];
}

interface ConnectedItemsState {
   showStatusDialogue: boolean;
   refreshResponses: GroupedItems;
   refreshRequests: string[];
   operationType: OperationType;
   updateRefreshResponses: (refreshResponse: RefreshResponse[]) => void;
   updateRefreshRequests: (itemNos: string[], type?: OperationType) => void;
   getAllItems: () => string[];
   getItemsCompleted: () => RefreshResponseItem[];
   getChildrenForParent: (parentItemNo: string) => RefreshResponseItem[];
   getParentItems: () => string[];
   getItemResponseStatus: (itemNo: string) => RefreshResponseItem | undefined;
   raiseRequestErrors: () => void;
   hasErrors: () => boolean;
   clearResult: () => void;
   closeDialogue: () => void;
   getOperationType: () => OperationType;
}

export const useConnectedItemsState = create<ConnectedItemsState>((set, get) => ({
   showStatusDialogue: false,
   refreshResponses: {},
   refreshRequests: [],
   operationType: 'refresh',

   updateRefreshResponses: (refreshResponses: RefreshResponse[]): void => {
      if (get().refreshRequests.length > 0) {
         set((state) => {
            const updatedResponses = { ...state.refreshResponses };

            refreshResponses.forEach((response) => {
               Object.entries(response.items).forEach(([parentItemNo, items]) => {
                  if (parentItemNo === '' && state.operationType === 'refresh') {
                     items.forEach((item) => {
                        updatedResponses[item.itemNo] = [];
                     });
                  } else {
                     updatedResponses[parentItemNo] = items;
                  }
               });
            });

            return { refreshResponses: updatedResponses };
         });
      }
   },

   updateRefreshRequests: (itemNos: string[], type: OperationType = 'refresh'): void => {
      set({
         refreshRequests: itemNos,
         showStatusDialogue: true,
         operationType: type,
      });
   },

   getAllItems: (): string[] => {
      const responses = get().refreshResponses;
      const parentItems = Object.keys(responses);
      const childItems = Object.values(responses)
         .flat()
         .map((item) => item.itemNo);
      return [...new Set([...parentItems, ...childItems])];
   },

   getItemsCompleted: (): RefreshResponseItem[] => {
      const responses = get().refreshResponses;
      const childItems = Object.values(responses).flat();

      if (get().operationType === 'refresh') {
         const selfCompletedItems: RefreshResponseItem[] = Object.entries(responses)
            .filter(([, children]) => children.length === 0)
            .map(([itemNo]) => ({ itemNo, status: true, message: 'Refreshed' }));
         return [...childItems, ...selfCompletedItems];
      }

      return childItems;
   },

   getChildrenForParent: (parentItemNo: string): RefreshResponseItem[] => {
      return get().refreshResponses[parentItemNo] || [];
   },

   getParentItems: (): string[] => {
      return Object.keys(get().refreshResponses);
   },

   getItemResponseStatus: (itemNo: string): RefreshResponseItem | undefined => {
      if (itemNo in get().refreshResponses) {
         if (get().operationType === 'refresh') {
            return {
               itemNo,
               status: true,
               message: 'Refreshed',
            };
         }
         return undefined;
      }

      const allChildItems = get().getItemsCompleted();
      return allChildItems.find((item) => item.itemNo === itemNo);
   },

   raiseRequestErrors: (): void => {
      const requests = get().refreshRequests;
      const errorResponse: RefreshResponse = {
         items: {},
      };

      requests.forEach((itemNo) => {
         errorResponse.items[itemNo] = [
            {
               itemNo,
               status: false,
               message: 'Network error',
            },
         ];
      });

      get().updateRefreshResponses([errorResponse]);
   },

   hasErrors: (): boolean => {
      return Object.values(get().refreshResponses).some((items) => items.some((item) => !item.status));
   },

   clearResult: (): void => {
      set({
         refreshRequests: [],
         refreshResponses: {},
         operationType: 'refresh',
      });
   },

   closeDialogue: (): void => {
      set({ showStatusDialogue: false });
   },

   getOperationType: (): OperationType => {
      return get().operationType;
   },
}));
